/**
 * The top island: the one stack of small things that drop in under the status bar on phones (the minimized
 * call pill, the offline pill, the in-app push banner). Each registers the height it takes while it shows and
 * reads its offset, so they stack in a fixed order instead of drawing over each other:
 *
 *   call pill → offline pill → banner
 *
 * The total (including the gaps) is published as `--lh-top-island` on <html> for pages that want to keep their
 * own top content clear while something shows.
 */
import { computed, getCurrentScope, onScopeDispose, reactive, toValue, watch, type ComputedRef, type MaybeRefOrGetter } from 'vue'

export const TOP_ISLAND_ORDER = { call: 0, offline: 1, banner: 2 } as const
export type TopIslandSlot = keyof typeof TOP_ISLAND_ORDER
/** Space between stacked items, px. */
export const TOP_ISLAND_GAP = 6

const heights = reactive<Record<TopIslandSlot, number>>({ call: 0, offline: 0, banner: 0 })

/** px the item in `slot` moves down to clear everything above it. */
export function topIslandOffset(slot: TopIslandSlot, h: Readonly<Record<TopIslandSlot, number>> = heights): number {
    let off = 0
    for (const s of Object.keys(TOP_ISLAND_ORDER) as TopIslandSlot[]) {
        if (TOP_ISLAND_ORDER[s] < TOP_ISLAND_ORDER[slot] && h[s] > 0) off += h[s] + TOP_ISLAND_GAP
    }
    return off
}

/** Everything showing, with gaps (0 when nothing is). */
export const topIslandHeight = computed(() => {
    const showing = (Object.keys(heights) as TopIslandSlot[]).filter((s) => heights[s] > 0)
    return showing.reduce((sum, s) => sum + heights[s], 0) + Math.max(0, showing.length - 1) * TOP_ISLAND_GAP
})

if (typeof document !== 'undefined') {
    watch(topIslandHeight, (h) => {
        if (h > 0) document.documentElement.style.setProperty('--lh-top-island', `${h + TOP_ISLAND_GAP}px`)
        else document.documentElement.style.removeProperty('--lh-top-island')
    })
}

/**
 * Take `height` px in the island while it's > 0 (released with the calling scope) and get this slot's offset.
 * Pass `0` to only read the offset.
 */
export function useTopIsland(slot: TopIslandSlot, height: MaybeRefOrGetter<number> = 0): ComputedRef<number> {
    watch(() => Math.max(0, Math.round(toValue(height) || 0)), (h) => { heights[slot] = h }, { immediate: true })
    if (getCurrentScope()) onScopeDispose(() => { heights[slot] = 0 })
    return computed(() => topIslandOffset(slot))
}
